import {observer} from 'mobx-react';
import PropTypes from 'prop-types';
// eslint-disable-next-line max-len
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {MapContext, ThemeContext} from '../../apiContexts';
import {useMap} from '../../components/map/hooks';
import useMapAlarm from '../../components/map/hooks/useMapAlarm';
import useMapDevice from '../../components/map/hooks/useMapDevice';
import useMapGeofences from '../../components/map/hooks/useMapGeofences';
import useMapLabel from '../../components/map/hooks/useMapLabel';
import useMapPosition from '../../components/map/hooks/useMapPosition';
import useMapReport from '../../components/map/hooks/useMapReport';
import useMapTrace from '../../components/map/hooks/useMapTraceV2';
import {initSearchData} from '../../components/search-box';
import useDevices from '../../hooks/use-devices';
import useStore from '../../hooks/use-store';
import history from '../../routes/history';
import {logOut} from '../../services/authService';
import '../../views/big-fleet/big-fleet.sass';
import Bottom from '../../views/big-fleet/bottom';
import Top from '../../views/big-fleet/top';
import FloatingReport from '../../views/reports/floating-report';
import {useIntl} from 'react-intl';
import useAlerts from '../../hooks/use-alerts';
import {notifyDeviceGoneAndReload} from '../../helpers/notifyDeviceGoneAndReload';
import {selectDeviceOnMap} from '../../helpers/selectDeviceOnMap';
import {goToReportRow, selectedRowCoordinate, startsNewReport} from '../../helpers/report';
import useServices from '../../hooks/use-services';
import {useSubjectsRealtimeUpdates} from 'src/hooks/use-subjets-realtime-updates.js';

const Map = styled.div`
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
`;

const BigFleetContainer = (props) => {
    const {
        location,
    } = props;

    const intl = useIntl();
    const {store} = useStore();
    const {driverService, commandService} = useServices();
    const theme = useContext(ThemeContext);
    const mapRef = useRef();
    const lastRowZoom = useRef(null);
    const {devices} = useDevices();
    const [report, setReport] = useState();
    const [isOpenModalCommand, toggleModalCommand] = useState(false);
    const [commandData, setCommandData] = useState({});
    const [searchData, setSearchData] = useState(initSearchData);
    const {toast} = useAlerts();

    const query = useMemo(
      () => ({search: location.search, hash: location.hash}),
      [location.search],
    );

    useSubjectsRealtimeUpdates();

    const {
        instance,
        trace,
        selectedDeviceTrace,
        toggleTrace,
        toggleSelectedDeviceTrace,
        label,
        toggleLabel,
        selectedMap,
        setSelectedMap,
        setReportData,
        showDevices,
        setShowDevices: toggleDevice,
        showGeofences,
        setShowGeofences,
        follow,
        setFollow,
        isAlarmsVisible,
        toggleAlarmsVisibility,
        createGeofenceForCurrentDevice,
        focusEventGeofence,
        clearGeofenceFocus,
    } = useMap(
      {
          target: mapRef,
          center: store.map.olCenter,
          zoom: store.map.zoom,
          tiers: store.map.olCustomTiles,
          initAllDevices: true,
          centerOnCluster: true,
          cluster: {
              distance: 25,
          },
      },
      useMapDevice,
      useMapPosition,
      useMapTrace,
      useMapLabel,
      useMapReport,
      useMapGeofences,
      useMapAlarm,
    );

    // REPORTS
    const {
        selectedItem,
        data,
        setData,
        setSelectedRow,
        selectedRow,
        setProps,
    } = useContext(MapContext);

    useEffect(() => {
        if (query.search) {
            setReport(query.hash.slice(1));
        } else {
            setReport(null);
        }
    }, [query]);

    // Fechar o relatorio (botao ou navegacao) tem que devolver o mapa ao normal -- sem isso
    // MapContext.data ficava com o ultimo relatorio pra sempre, e dispositivo/alarme/label/trilha
    // continuavam escondidos mesmo depois de fechar.
    useEffect(() => {
        if (!report) {
            setData([]);
        }
    }, [report]);

    useEffect(() => {
        (async () => {
            setSearchData({
                ...searchData,
                options: devices.list.map(({id, uniqueId, name}) => ({
                    id,
                    uniqueId: uniqueId,
                    name,
                })),
            });
        })();
    }, [devices.list]);

    useEffect(() => {
        driverService.list().then((drivers) => {
            store.drivers.setDrivers(drivers);
        });
    }, []);

    /**
     * @param {number} deviceId dispositivo escolhido
     * @param {Object} [event] evento de cerca (vem do sino): o mapa vai à cerca
     *   citada com a posição do evento; sem cerca visível ao usuário, vai ao veículo (`selectDeviceOnMap`)
     */
    const setDeviceSelected = (deviceId, event) => {
        if (!devices.devicesMap.has(deviceId)) {
            notifyDeviceGoneAndReload(toast, intl);
            return;
        }
        selectDeviceOnMap({devices, deviceId, event, instance, focusEventGeofence, clearGeofenceFocus, setFollow});
    };

    const searchDevice = (data) => {
        if (data.length) {
            const {id} = data[0];
            setDeviceSelected(id);
        }
    };

    useEffect(() => {
        if (instance) {
            setProps({map: instance});
            const map = instance.instance;
            instance.setI18n(intl);

            map.on('click', (event) => {
                const [feature, layer] =
                map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
                    return [feature, layer];
                }) || [];

                if (!layer) return;

                const layerName = layer.get('name');
                if (layerName === 'reportLayer') {
                    const featureName = feature.get('name');
                    if (featureName !== 'line') {
                        setSelectedRow({
                            id: feature.get('id') || feature.getId(),
                            scroll: true,
                        });
                    }
                }

                if (layerName === 'devicesLayer') {
                    const features = feature.get('features');
                    if (features.length === 1) {
                        setDeviceSelected(features[0].getId());
                    } else {
                        instance.spiderfyCluster(feature);
                    }
                }
            });

            map.on('pointermove', (event) => {
                map.getTargetElement().style.cursor = map.hasFeatureAtPixel(event.pixel) ?
                  'pointer' :
                  '';
            });

            map.on('pointerdrag', (event) => {
                instance.isFollowAnimating = false;
                setFollow(false);
            });

            let zoomCache;
            map.on('movestart', (event) => {
                zoomCache = event.map.getView().getZoom();
            });

            map.on('moveend', (event) => {
                if (instance.isFollowAnimating) {
                    instance.isFollowAnimating = false;
                    return;
                }
                const zoom = event.map.getView().getZoom();
                zoom !== zoomCache && setFollow(false);
            });
        }
    }, [instance]);

    useEffect(() => {
        if (instance) {
            if (selectedRow) {
                instance.setSelectedReportPoint(selectedRow.id, selectedRow.deviceId, selectedRowCoordinate(selectedRow));
            }
        }
    }, [selectedRow]);

    useEffect(() => {
        if (instance) {
            if (selectedItem) {
                const {longitude, latitude} = selectedItem;
                if (longitude && latitude) {
                    goToReportRow(instance, [longitude, latitude], lastRowZoom);
                } else {
                    instance.goTo(null, instance._instance.getView().getZoom() - 0.0001);
                }
            }
        }
    }, [selectedItem]);

    useEffect(() => {
        if (startsNewReport(data)) lastRowZoom.current = null; // a primeira linha de um relatorio novo usa o zoom do relatorio
        setReportData(data);
    }, [data]);

    // END REPORTS

    const followToogle = () => {
        const status = !follow;
        setFollow(status);
        if (status && devices.selectedDevice.hasPosition()) {
            const {longitude, latitude} = devices.selectedDevice.currentPosition;
            instance.goTo([longitude, latitude]);
        }
    };

    const onChangeMap = (value) => {
        setSelectedMap(value);
    };

    const toggleGeofences = () => {
        setShowGeofences(!showGeofences);
    };

    const close = () => {
        instance.removeReports();
        history.push('/fleet');
    };

    const block = (status) => {
        const {selectedDevice} = devices;
        const {id} = selectedDevice;
        const data = {deviceId: id, type: status ? 'engineStop' : 'engineResume'};
        setCommandData(data);
        toggleModalCommand(true);
    };

    const sendCommand = async () => {
        await commandService.send(commandData);
        toast.success(intl.formatMessage({id: 'commandSent'}));
        toggleModalCommand(false);
    };

    const closeDetail = () => {
        store.devices.setSelected(null);
    };

    return (
      <div id="bigFleet">
          <Map ref={mapRef}/>

          <Top
            logOut={logOut}
            events={store.events}
            searchData={{...searchData, onChange: searchDevice}}
            setDeviceSelected={setDeviceSelected}
          />
          <div className="content"/>

          <Bottom
            map={instance}
            devices={devices}
            theme={theme}
            toast={toast}
            intl={intl}
            auth={store.auth}
            server={store.server}
            drivers={store.drivers}
            setSelected={setDeviceSelected}
            selectedDevice={store.devices.selectedDevice}
            updatedPositions={store.devices.updatedPositions}
            isOpenModalCommand={isOpenModalCommand}
            toggleModalCommand={toggleModalCommand}
            sendCommand={sendCommand}
            commandData={commandData}
            block={block}
            close={closeDetail}
            {...{
                trace,
                selectedDeviceTrace,
                toggleTrace,
                toggleSelectedDeviceTrace,
                follow,
                followToogle,
                toggleLabel,
                label,
                toggleDevice,
                showDevices,
                onChangeMap,
                selectedMap,
                createGeofence: createGeofenceForCurrentDevice,
                showGeofences,
                toggleGeofences,
                isAlarmsVisible,
                toggleAlarmsVisibility,
            }}
          />

          {report && (
            <FloatingReport
              type={report}
              location={location}
              close={close}
            />
          )}
      </div>
    );
};

export default observer(BigFleetContainer);

BigFleetContainer.propTypes = {
    location: PropTypes.any,
};
